import React, { useState, useCallback, useMemo } from 'react';
import { observer } from 'mobx-react';

import {
  Box,
  Badge,
  Drawer,
  IconButton,
  Typography,
  CircularProgress,
  styled,
} from '@mui/material';
import { Notifications as NotificationsIcon, Close as CloseIcon } from '@mui/icons-material';
import { useStore } from 'hooks';

import { useNavigate } from 'react-router-dom';
import { SettingsIcon } from 'assets/icons';

import Dialog from 'components/shared/dialogs/Dialog';
import ButtonsGroup from 'components/shared/ButtonsGroup';

import Item from './Item';
import AlertDetails from 'components/shared/AlertDetails';

import palette from 'theme/palette';
import { useTranslation } from 'react-i18next';

const NotificationsMenu = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { notificationsStore } = useStore();

  const [open, setOpen] = useState(false);
  const [currentCondition, setCurrentCondition] = useState(null);
  const [openDetailsModal, setOpenDetailsModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const openNotifications = useCallback(e => {
    setOpen(Boolean(e.currentTarget));
  }, []);

  const handleClose = useCallback(() => {
    setOpen(false);
  }, []);

  const handleClickSetting = useCallback(() => {
    setOpen(false);
    navigate('/my-account/notification-settings');
  }, [navigate]);

  const handleConditionStateFilterChange = useCallback(
    value => {
      notificationsStore.setSelectedConditionStateFilter(value);
    },
    [notificationsStore],
  );

  const handleMarkAll = useCallback(async () => {
    const ids = notificationsStore.unread.map(({ id }) => id);

    if (ids.length === 0 || isLoading) return;

    try {
      setIsLoading(true);
      await notificationsStore.markAsRead(ids);
    } finally {
      setIsLoading(false);
    }
  }, [notificationsStore, isLoading]);

  const openModalDetails = useCallback(
    notification => {
      if (notification?.condition && notification.condition.type.includes('Offline')) {
        navigate('/home', {
          replace: true,
          state: { isModalOpen: true, conditionId: notification.condition.id },
        });
        handleClose();
      } else {
        setCurrentCondition(notification.condition);
        setOpenDetailsModal(true);
      }
    },
    [handleClose, navigate],
  );

  const viewSensorDetails = useCallback(() => {
    navigate(`/quality-management/system-health`);
    setOpen(false);
    setOpenDetailsModal(false);
  }, [navigate]);

  const CONDITION_STATE_FILTER_OPTIONS = [
    { name: t('shared.NotificationsMenu.all'), value: null },
    { name: t('shared.NotificationsMenu.active'), value: 'active' },
    { name: t('shared.NotificationsMenu.resolved'), value: 'resolved' },
  ];

  const MarkAllButton = useMemo(
    () => (
      <Typography
        onClick={handleMarkAll}
        sx={{
          color: notificationsStore.unreadCount ? palette.primary.main : palette.whiteEmphasis.low,
          cursor: notificationsStore.unreadCount ? 'pointer' : 'default',
          padding: 0,
          textTransform: 'none',
          minWidth: '115px',
          textAlign: 'center',
        }}
      >
        {isLoading ? <CircularProgress size={20} /> : t('shared.NotificationsMenu.markAllAsRead')}
      </Typography>
    ),
    [t, notificationsStore.unreadCount, handleMarkAll, isLoading],
  );

  const RootBox = (
    <StyledBox className="root_box">
      <Box className="header_box">
        <CloseIcon className="clickable" onClick={handleClose} />
        {MarkAllButton}
      </Box>
      <Box className="main_box">
        <Box className="title_box">
          <Typography fontSize={24}>{t('shared.NotificationsMenu.notifications')}</Typography>
          <IconButton onClick={handleClickSetting}>
            <SettingsIcon className="settings_icon" />
          </IconButton>
        </Box>
        <ButtonsGroup
          options={CONDITION_STATE_FILTER_OPTIONS}
          onChange={handleConditionStateFilterChange}
          selected={notificationsStore.selectedConditionStateFilter}
          mb={2}
        />
        <Box className="notifications_list">
          {notificationsStore.filtered.length ? (
            notificationsStore.filtered.map((notification, index) => (
              <Item
                key={index}
                notification={notification}
                index={index}
                onItemClick={() => openModalDetails(notification)}
              />
            ))
          ) : (
            <Typography display="flex" justifyContent="center" mt={1}>
              {t('shared.NotificationsMenu.noNewNotifications')}
            </Typography>
          )}
        </Box>
      </Box>
    </StyledBox>
  );

  const Mobile = (
    <StyledRootDialog
      open={open}
      title=""
      fullWidth
      maxWidth="xs"
      onClose={handleClose}
      mobileHeaderCenter={false}
      mobileHeaderRight={MarkAllButton}
    >
      {RootBox}
    </StyledRootDialog>
  );

  const Desktop = (
    <StyledDrawer anchor="right" open={open} onClose={handleClose}>
      {RootBox}
    </StyledDrawer>
  );

  return (
    <>
      <IconButton
        id="notification_icon"
        edge="end"
        aria-label="notifications"
        className="notificationsIcon"
        onClick={openNotifications}
        sx={theme => ({
          [theme.breakpoints.down('sm')]: {
            paddingRight: '12px',
          },
        })}
      >
        <Badge
          color="error"
          invisible={!notificationsStore.unreadCount}
          badgeContent={notificationsStore.unreadCount}
        >
          <NotificationsIcon />
        </Badge>
      </IconButton>
      {Mobile}
      {open && Desktop}

      {openDetailsModal && (
        <AlertDetails
          condition={currentCondition}
          onViewSensorDataClick={viewSensorDetails}
          onClose={() => setOpenDetailsModal(false)}
        />
      )}
    </>
  );
};

export default observer(NotificationsMenu);

const StyledBox = styled(Box)(({ theme: { palette, breakpoints } }) => ({
  width: '320px',
  padding: '16px',

  [breakpoints.down('sm')]: {
    width: '100%',
  },

  '.header_box': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',

    [breakpoints.down('sm')]: {
      display: 'none',
    },
  },

  '.main_box': {
    display: 'flex',
    flexDirection: 'column',
  },

  '.title_box': {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '24px',
    marginBottom: '16px',

    [breakpoints.down('sm')]: {
      marginTop: 0,
    },

    p: {
      color: palette.whiteEmphasis.high,
    },
  },

  '.clickable_icon': {
    cursor: 'pointer',
  },

  '.settings_icon': {
    color: palette.whiteEmphasis.high,
  },

  '.notifications_list': {
    marginTop: '16px',
    maxHeight: 'calc(100vh - 200px)',
    overflowY: 'auto',
  },
}));

const StyledDrawer = styled(Drawer)(({ theme: { palette, breakpoints } }) => ({
  [breakpoints.down('sm')]: {
    display: 'none',
  },
}));

const StyledRootDialog = styled(Dialog)(({ theme: { palette, breakpoints } }) => ({
  [breakpoints.down('sm')]: {
    overflow: 'hidden',
  },
  [breakpoints.up('sm')]: {
    display: 'none',
  },

  '.titleDark': {
    background: palette.background.main,
  },
}));
